import { ComponentType } from 'react';
import {
  areSkillsAndBehaviorsPagesEnabled,
  atLeastOneContinuousFeedbackFeatureIsEnabled,
  calibrationDashboardIsEnabled,
  engagementIsEnabled,
  isAIAgentsEnabled,
  isSkillsAndBehaviorsDirectoryEnabled,
  keyTakeawaysIsEnabled,
  objectivesAreEnabled,
  oneOnOnesAreEnabled,
  participationDashboardIsEnabled,
  performanceFlowForEndUsersIsEnabled,
  promotionPacketsAreEnabled,
  ratingDashboardIsEnabled,
  surveysAndPerformanceNavIsEnabled,
} from 'utils/util/features';
import CustomSurveyConfigurationPage from 'views/Administration/CustomSurveys/CustomSurveyConfigurationPage';
import CustomSurveyParticipantResponsePage from 'views/Administration/CustomSurveys/CustomSurveyParticipantResponsePage';
import CustomSurveyQuestionAnswersPage from 'views/Administration/CustomSurveys/CustomSurveyQuestionAnswersPage';
import CustomSurveyResponsesPage from 'views/Administration/CustomSurveys/CustomSurveyResponsesPage';
import CustomSurveysAdministrationPage from 'views/Administration/CustomSurveys/CustomSurveysAdministrationPage';
import CustomSurveyReportEditorPage from 'views/Administration/CustomSurveys/Reports/CustomSurveyReportEditorPage';
import CustomSurveyReportPage from 'views/Administration/CustomSurveys/Reports/CustomSurveyReportPage';
import DevelopmentPlansAdministrationPage from 'views/Administration/DevelopmentPlansAdministrationPage';
import FeaturesAdministrationPage from 'views/Administration/FeaturesAdministrationPage';
import FeedbackAndRecognitionAdministrationPage from 'views/Administration/FeedbackAndRecognitionAdministrationPage';
import { ObjectivesAdministrationPage } from 'views/Administration/ObjectivesAdmin';
import OneOnOnesAdministrationPage from 'views/Administration/OneOnOnesAdministrationPage';
import OrganizationAdministrationPage from 'views/Administration/OrganizationAdministrationPage';
import PromotionPacketsAdministrationPage from 'views/Administration/PromotionPacketsAdministrationPage';
import AIAgentsHub from 'views/AI/Agents/AIAgentsHub';
import TeamAIPromptsPage from 'views/AI/TeamAIPromptsPage';
import CustomSurveyResponsePage from 'views/CustomSurveys/CustomSurveyResponsePage';
import SharedSurveyReportsPage from 'views/CustomSurveys/SharedSurveyReportsPage';
import DevelopmentPlanPage from 'views/DevelopmentPlans/DevelopmentPlanPage';
import NudgesList from 'views/Nudges/NudgesList';
import SentryTest from 'views/Test/SentryTest';
import * as consts from './consts/consts';
// NOTE: we don't use React Lazy loading to improve navigation performance between pages
import Account from './views/Account/Account';
import ActivitiesDirectoryPage from './views/Activities/ActivitiesDirectoryPage';
import ActivityPage from './views/Activities/ActivityPage';
import CampaignAdministrationDashboard from './views/Administration/CampaignAdministrationDashboard';
import CampaignEditor from './views/Administration/CampaignEditor';
import PeopleAdministrationHubPage from './views/Administration/PeopleAdministrationHubPage';
import AppIntegrations from './views/AppIntegrations/AppIntegrations';
import CredentialPage from './views/Credentials/CredentialPage';
import CredentialsDirectoryPage from './views/Credentials/CredentialsDirectoryPage';
import Dashboard from './views/Dashboard/Dashboard';
import DevelopmentPlansDashboard from './views/DevelopmentPlans/DevelopmentPlansDashboard';
import EngagementHubPage from './views/Engagement/EngagementHubPage';
import FeedbackDirectoryPage from './views/Feedback/FeedbackDirectoryPage';
import Invitation from './views/Invitations/Invitation';
import Logout from './views/Layout/Logout';
import PageMaintenance from './views/Layout/Pages/Errors/PageMaintenance';
import RedirectToHome from './views/Layout/RedirectToHome';
import JobFamily from './views/Levels/JobFamily';
import LevelingFramework from './views/Levels/LevelingFramework';
import ObjectivesHubPage from './views/Objectives/ObjectivesHubPage';
import OneOnOnesDirectoryPage from './views/OneOnOnes/OneOnOnesDirectoryPage';
import PeopleDirectoryPage from './views/People/PeopleDirectoryPage';
import PerformanceAdministrationPeerSelection from './views/Performance/PerformanceAdministrationPeerSelection';
// Leveling and performance pages
import PerformanceCurrentStep from './views/Performance/PerformanceCurrentStep';
import PerformanceHubPage from './views/Performance/PerformanceHubPage';
import MyProfile from './views/Person/MyProfile';
import PersonProfile from './views/Person/PersonProfile';
import PlatformIdProfile from './views/Person/PlatformIdProfile';
import PromotionPacketPage from './views/PromotionPackets/PromotionPacketPage';
import PromotionPacketsDashboard from './views/PromotionPackets/PromotionPacketsDashboard';
import Referrals from './views/Referrals/Referrals';
import SkillPage from './views/Skills/SkillPage';
import SkillsDirectoryPage from './views/Skills/SkillsDirectoryPage';
import TasksPage from './views/Tasks/TasksPage';
// Team management pages
import TeamPage from './views/Team/TeamPage';
import I18nTest from './views/Test/I18nTest';
import LocalStorageContentTest from './views/Test/LocalStorageContentTest';
import WelcomeStep0 from './views/Welcome/WelcomeStep0';
import WelcomeStep1 from './views/Welcome/WelcomeStep1';

export interface PermissionCallbackArgs {
  isHRBP: boolean;
  isOrgSystemAdmin: boolean;
  isSuperAdmin: boolean;
  isOneOnOneAdmin: boolean;
  isAIAdmin: boolean;
  isObjectivesAdmin: boolean;
  isPromotionPacketAdmin: boolean;
  isDevelopmentPlanAdmin: boolean;
  hasDirectReports: boolean;
}

export interface PermissionCallbackResult {
  isEnabled: boolean;
}
export type PermissionCallback = (
  permissionArgs: PermissionCallbackArgs
) => PermissionCallbackResult;

export interface Route {
  path: string;
  name: string;
  exact: boolean;
  component: ComponentType;
  permissionsCallback?: PermissionCallback;
}

const adminRequired = ({ isOrgSystemAdmin, isSuperAdmin }) => ({
  isEnabled: isOrgSystemAdmin || isSuperAdmin,
});

const oneOnOneAdminRequired = ({ isSuperAdmin, isOneOnOneAdmin }) => ({
  isEnabled: isOneOnOneAdmin || isSuperAdmin,
});

const feedbackAndRecognitionAdminRequired = ({ isSuperAdmin, isHRBP }) => ({
  isEnabled: isHRBP || isSuperAdmin,
});

const promotionPacketAdminRequired = ({
  isSuperAdmin,
  isPromotionPacketAdmin,
}) => ({
  isEnabled: isPromotionPacketAdmin || isSuperAdmin,
});

const developmentPlanAdminRequired = ({
  isSuperAdmin,
  isDevelopmentPlanAdmin,
}) => ({
  isEnabled: isDevelopmentPlanAdmin || isSuperAdmin,
});

const aiAdminRequired = ({ isSuperAdmin, isAIAdmin }) => ({
  isEnabled: isAIAdmin || isSuperAdmin,
});

const objectivesAdminRequired = ({ isSuperAdmin, isObjectivesAdmin }) => ({
  isEnabled: isObjectivesAdmin || isSuperAdmin,
});

// Mirrors can_manage_custom_surveys() in admin/api/logic/custom_surveys.py:
// superuser, HRBP, or a manager with active direct reports (CONF-434).
// System admins are deliberately excluded - custom surveys are HRBP-owned,
// not part of the general admin role.
const customSurveysAdminRequired = ({
  isHRBP,
  isSuperAdmin,
  hasDirectReports,
}) => ({
  isEnabled: isHRBP || isSuperAdmin || hasDirectReports,
});

interface RouteDescriptorItem {
  name: string;
  path: string;
}

interface RouteDescriptor {
  route: RouteDescriptorItem;
  component: ComponentType;
  subroute?: string;
  exact?: boolean;
  permissionsCallback?: PermissionCallback;
}

const generateRoutes = (features, formatMessage): Route[] => {
  // Routes included for everyone regardless of which features are enabled
  // NOTE: Also add any new route paths to core/admin/api/utils.py: APP_ROUTES
  let routes: RouteDescriptor[] = [
    { route: consts.MAINTENANCE(formatMessage), component: PageMaintenance },
    { route: consts.LOGOUT(formatMessage), component: Logout },
    { route: consts.WELCOME(formatMessage), component: WelcomeStep0 },
    { route: consts.WELCOME_STEP_1(formatMessage), component: WelcomeStep1 },
    { route: consts.LOGIN(formatMessage), component: RedirectToHome },
    { route: consts.SIGNUP(formatMessage), component: RedirectToHome },
    { route: consts.HOME(formatMessage), component: Dashboard },
    { route: consts.TASKS(formatMessage), component: TasksPage },
    { route: consts.I18N_TEST(formatMessage), component: I18nTest },
    { route: consts.SENTRY_TEST(formatMessage), component: SentryTest },
    {
      route: consts.LOCAL_STORAGE_TEST(formatMessage),
      component: LocalStorageContentTest,
    },
    {
      route: consts.ACCOUNT(formatMessage),
      exact: false,
      // @ts-expect-error
      component: Account,
    },
    { route: consts.MY_PROFILE(formatMessage), component: MyProfile },
    {
      route: consts.ADMINISTRATION_PEOPLE(formatMessage),
      exact: false,
      component: PeopleAdministrationHubPage,
      permissionsCallback: adminRequired,
    },
    {
      route: consts.ADMINISTRATION_ORGANIZATION(formatMessage),
      component: OrganizationAdministrationPage,
      permissionsCallback: adminRequired,
    },
    {
      route: consts.ADMINISTRATION_FEATURES(formatMessage),
      exact: false,
      component: FeaturesAdministrationPage,
      permissionsCallback: adminRequired,
    },
    {
      route: consts.ADMINISTRATION_ONE_ON_ONES(formatMessage),
      exact: false,
      component: OneOnOnesAdministrationPage,
      permissionsCallback: oneOnOneAdminRequired,
    },
    {
      route: consts.ADMINISTRATION_FEEDBACK_AND_RECOGNITION(formatMessage),
      exact: false,
      component: FeedbackAndRecognitionAdministrationPage,
      permissionsCallback: feedbackAndRecognitionAdminRequired,
    },
    {
      route: consts.ADMINISTRATION_OBJECTIVES(formatMessage),
      exact: false,
      component: ObjectivesAdministrationPage,
      permissionsCallback: objectivesAdminRequired,
    },
    {
      route: consts.ADMINISTRATION_CAMPAIGNS(formatMessage),
      component: CampaignAdministrationDashboard,
      permissionsCallback: adminRequired,
    },
    {
      route: consts.ADMINISTRATION_CAMPAIGNS(formatMessage),
      subroute: '/:id',
      exact: false,
      component: CampaignEditor,
      permissionsCallback: adminRequired,
    },
  ];

  const engagementEnabled = engagementIsEnabled(features);
  const surveysAndPerfEnabled = surveysAndPerformanceNavIsEnabled(features);
  const perfFlowEnabled = performanceFlowForEndUsersIsEnabled(features);
  const ratingDashboardEnabled = ratingDashboardIsEnabled(features);
  const participationDashboardEnabled =
    participationDashboardIsEnabled(features);
  const calibrationDashboardEnabled = calibrationDashboardIsEnabled(features);
  const keyTakeawaysEnabled = keyTakeawaysIsEnabled(features);
  const objectivesEnabled = objectivesAreEnabled(features);
  const promoPacketsAreEnabled = promotionPacketsAreEnabled(features);
  const oneOnOnesEnabled = oneOnOnesAreEnabled(features);
  const anyContinuousFeedbackorRecognitionEnabled =
    atLeastOneContinuousFeedbackFeatureIsEnabled(features);
  const skillsAndBehaviorsDirectoryIsEnabled =
    isSkillsAndBehaviorsDirectoryEnabled(features);
  const skillsAndBehaviorsPagesAreEnabled =
    areSkillsAndBehaviorsPagesEnabled(features);
  const aiAgentsEnabled = isAIAgentsEnabled(features);

  // TODO: modularize these into features.ts like the above functions
  const peopleDirectoryOrOrgChartIsEnabled = features?.people?.enabled;
  const developmentPlansAreEnabled = features?.development_plans?.enabled;
  const templatesAdministrationEnabled =
    features?.content_administration?.enabled &&
    features?.content_administration?.templates_enabled;
  const nudgesAreEnabled = features?.nudges?.enabled;
  const levelsAreEnabled = features?.levels?.enabled;
  const activitiesAreEnabled = features?.activities?.enabled;
  const credentialsAreEnabled = features?.credentials?.enabled;
  const referralsAreEnabled = features?.referrals?.enabled;
  const customSurveysAreEnabled = features?.custom_surveys?.enabled;

  if (peopleDirectoryOrOrgChartIsEnabled) {
    routes = routes.concat([
      {
        route: consts.PEOPLE_ORG_CHART(formatMessage),
        component: PeopleDirectoryPage,
      },
      {
        route: consts.PEOPLE_DIRECTORY_LIST(formatMessage),
        component: PeopleDirectoryPage,
      },
    ]);
  }

  routes = routes.concat([
    {
      route: consts.INVITATIONS(formatMessage),
      subroute: '/:id',
      component: Invitation,
    },
    {
      route: consts.PEOPLE_SUBROUTE(formatMessage),
      subroute: '/:personId',
      exact: false,
      component: PersonProfile,
    },
    {
      route: consts.PLATFORM_ID_PROFILE(formatMessage),
      subroute: '/:platformId',
      exact: false,
      component: PlatformIdProfile,
    },
  ]);

  if (activitiesAreEnabled) {
    routes = routes.concat([
      {
        route: consts.ACTIVITIES_DIRECTORY(formatMessage),
        component: ActivitiesDirectoryPage,
      },
    ]);
  }

  // profiles are always enabled
  routes = routes.concat([
    {
      route: consts.MY_PROFILE(formatMessage),
      subroute: '/:subroute',
      component: MyProfile,
    },
  ]);

  /* TEAM */

  if (objectivesEnabled) {
    routes = routes.concat([
      {
        route: consts.COMPANY_OBJECTIVES(formatMessage),
        component: ObjectivesHubPage,
      },
      {
        route: consts.COMPANY_OBJECTIVES_TAB_STRATEGIC(formatMessage),
        component: ObjectivesHubPage,
      },
      {
        route: consts.COMPANY_OBJECTIVES_TAB_OVERVIEW(formatMessage),
        component: ObjectivesHubPage,
      },
      {
        route: consts.COMPANY_OBJECTIVES_TAB_PARTICIPATION(formatMessage),
        component: ObjectivesHubPage,
      },
    ]);
  }

  routes = routes.concat([
    // team page is always enabled
    { route: consts.TEAM(formatMessage), component: TeamPage },
  ]);

  if (aiAgentsEnabled) {
    routes = routes.concat([
      {
        route: consts.TEAM_AI_PROMPTS(formatMessage),
        component: TeamAIPromptsPage,
      },
    ]);
  }

  if (engagementEnabled) {
    routes = routes.concat([
      {
        route: consts.ENGAGEMENT_HUB(formatMessage),
        component: EngagementHubPage,
      },
    ]);
  }

  if (surveysAndPerfEnabled) {
    /* PERFORMANCE DASHBOARDS PATHS (SHOULD COME BEFORE PERFORMANCE CYCLE STEPS)
       DUE TO EXACT MATCHING */
    routes = routes.concat([
      {
        route: consts.PERFORMANCE_ADMINISTRATION_PEER_SELECTION(formatMessage),
        component: PerformanceAdministrationPeerSelection,
      },
      // note: even if teamDashboardEnabled is false, we want to still
      // include this route so visiting /performance/admin will redirect
      // to the first tab enabled, if any, instead of giving a 404
      {
        route: consts.TEAM_PERFORMANCE_DASHBOARD(formatMessage),
        component: PerformanceHubPage,
      },
      ...(participationDashboardEnabled
        ? [
            {
              route: consts.PARTICIPATION_DASHBOARD(formatMessage),
              component: PerformanceHubPage,
            },
          ]
        : []),
      ...(ratingDashboardEnabled
        ? [
            {
              route: consts.RATINGS_DASHBOARD(formatMessage),
              component: PerformanceHubPage,
            },
          ]
        : []),
      ...(calibrationDashboardEnabled
        ? [
            {
              route: consts.CALIBRATION_DASHBOARD(formatMessage),
              subroute: '/:viewId(\\d+)',
              exact: true,
              component: PerformanceHubPage,
            },
            {
              route: consts.CALIBRATION_DASHBOARD(formatMessage),
              component: PerformanceHubPage,
            },
          ]
        : []),
      ...(keyTakeawaysEnabled
        ? [
            {
              route: consts.TAKEAWAYS_DASHBOARD(formatMessage),
              component: PerformanceHubPage,
            },
          ]
        : []),
    ]);
  }

  if (developmentPlansAreEnabled) {
    routes = routes.concat([
      {
        route: consts.DEVELOPMENT_PLANS(formatMessage),
        subroute: '/:id',
        exact: false,
        component: DevelopmentPlanPage,
      },
      {
        route: consts.DEVELOPMENT_PLANS(formatMessage),
        component: DevelopmentPlansDashboard,
      },
    ]);
  }

  // These admin sections only hold templates and their audit trail, so they
  // do not exist without the templates flag
  if (templatesAdministrationEnabled) {
    routes = routes.concat([
      {
        route: consts.ADMINISTRATION_PROMOTION_PACKETS(formatMessage),
        exact: false,
        component: PromotionPacketsAdministrationPage,
        permissionsCallback: promotionPacketAdminRequired,
      },
      {
        route: consts.ADMINISTRATION_DEVELOPMENT_PLANS(formatMessage),
        exact: false,
        component: DevelopmentPlansAdministrationPage,
        permissionsCallback: developmentPlanAdminRequired,
      },
    ]);
  }

  if (promoPacketsAreEnabled) {
    routes = routes.concat([
      {
        route: consts.PROMOTION_PACKETS(formatMessage),
        subroute: '/:id',
        exact: false,
        component: PromotionPacketPage,
      },
      {
        route: consts.PROMOTION_PACKETS(formatMessage),
        component: PromotionPacketsDashboard,
      },
    ]);
  }

  if (perfFlowEnabled) {
    routes = routes.concat([
      /* PERFORMANCE CYCLE STEPS (MUST COME AFTER PERFORMANCE ADMINISTRATIONS) */
      // NOTE: if the below paths changes, please ensure to update Layout.js to reflect
      // these changes to ensure that the main left nav is hidden and top header shows
      // a simplified version to minimize distractions and escape paths.
      {
        // for campaign PREVIEWING (for admins to walk through the flow of a campaign)
        route: consts.PREVIEW_CAMPAIGN(formatMessage),
        // no step in url means go to the current step
        subroute: '/:campaignId/performance/:step?',
        exact: false,
        component: (props) => (
          <PerformanceCurrentStep isPreview={true} {...props} />
        ),
        permissionsCallback: adminRequired,
      },
      {
        route: consts.PERFORMANCE(formatMessage),
        // no step in url means go to the current step
        subroute: '/:step?',
        exact: false,
        component: PerformanceCurrentStep,
      },
    ]);
  }

  /* NON PERFORMANCE-RELATED PATHS */

  if (aiAgentsEnabled) {
    routes = routes.concat([
      {
        route: consts.AI(formatMessage),
        exact: false,
        component: AIAgentsHub,
        permissionsCallback: aiAdminRequired,
      },
    ]);
  }

  if (levelsAreEnabled) {
    routes = routes.concat([
      {
        route: consts.LEVELING_FRAMEWORK(formatMessage),
        component: LevelingFramework,
      },
    ]);
  }

  if (nudgesAreEnabled) {
    routes = routes.concat([
      {
        route: consts.NUDGES_ADMINISTRATION(formatMessage),
        component: NudgesList,
      },
    ]);
  }

  if (customSurveysAreEnabled) {
    routes = routes.concat([
      {
        // Ahead of the editor route below: that one is non-exact, so it would
        // otherwise match this report path's `/edit/:id` prefix first.
        route:
          consts.ADMINISTRATION_CUSTOM_SURVEYS_REPORT_EDITOR(formatMessage),
        exact: true,
        component: CustomSurveyReportEditorPage,
        permissionsCallback: customSurveysAdminRequired,
      },
      {
        route: consts.ADMINISTRATION_CUSTOM_SURVEYS_EDITOR(formatMessage),
        exact: false,
        component: CustomSurveyConfigurationPage,
        permissionsCallback: customSurveysAdminRequired,
      },
      {
        route:
          consts.ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES_PARTICIPANT(
            formatMessage
          ),
        exact: true,
        component: CustomSurveyParticipantResponsePage,
        permissionsCallback: customSurveysAdminRequired,
      },
      {
        route:
          consts.ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES_QUESTION(
            formatMessage
          ),
        exact: true,
        component: CustomSurveyQuestionAnswersPage,
        permissionsCallback: customSurveysAdminRequired,
      },
      {
        // Shared report-item render surface, not admin-only - a manager or
        // any viewer this specific item was shared with can open it once
        // published (see CustomSurveyReportItemViewSet.retrieve), so this
        // deliberately has no permissionsCallback.
        route: consts.CUSTOM_SURVEY_REPORT_ITEM(formatMessage),
        exact: true,
        component: CustomSurveyReportPage,
      },
      {
        route: consts.TEAM_LIFECYCLE_SURVEYS(formatMessage),
        exact: true,
        component: SharedSurveyReportsPage,
      },
      {
        route: consts.ADMINISTRATION_CUSTOM_SURVEYS_RESPONSES(formatMessage),
        exact: false,
        component: CustomSurveyResponsesPage,
        permissionsCallback: customSurveysAdminRequired,
      },
      {
        route: consts.ADMINISTRATION_CUSTOM_SURVEYS(formatMessage),
        exact: false,
        component: CustomSurveysAdministrationPage,
        permissionsCallback: customSurveysAdminRequired,
      },
      {
        // A respondent answering their own survey - not an admin surface.
        route: consts.CUSTOM_SURVEY_RESPONSE(formatMessage),
        exact: true,
        component: CustomSurveyResponsePage,
      },
    ]);
  }

  // always include account and integrations pages
  routes = routes.concat([
    // @ts-expect-error
    { route: consts.ACCOUNT(formatMessage), component: Account },
    {
      route: consts.APP_INTEGRATIONS(formatMessage),
      component: AppIntegrations,
      permissionsCallback: adminRequired,
    },
  ]);

  if (levelsAreEnabled) {
    routes = routes.concat([
      {
        route: consts.LEVELING_FRAMEWORK(formatMessage),
        subroute: '/:slug',
        component: JobFamily,
      },
    ]);
  }

  routes = routes.concat([
    {
      route: consts.INVITATIONS(formatMessage),
      subroute: '/:id',
      component: Invitation,
    },
  ]);

  if (activitiesAreEnabled) {
    routes = routes.concat([
      {
        route: consts.CONTRIBUTIONS(formatMessage),
        subroute: '/:contributionId',
        exact: false,
        component: ActivityPage,
      },
      {
        route: consts.ACTIVITIES(formatMessage),
        subroute: '/:id',
        exact: false,
        component: ActivityPage,
      },
    ]);
  }

  if (skillsAndBehaviorsDirectoryIsEnabled) {
    routes = routes.concat([
      {
        route: consts.SKILLS_DIRECTORY(formatMessage),
        component: SkillsDirectoryPage,
      },
    ]);
  }

  if (skillsAndBehaviorsPagesAreEnabled) {
    routes = routes.concat([
      {
        route: consts.SKILLS(formatMessage),
        subroute: '/:id',
        exact: false,
        component: SkillPage,
      },
    ]);
  }

  if (credentialsAreEnabled) {
    routes = routes.concat([
      {
        route: consts.CREDENTIALS_DIRECTORY(formatMessage),
        component: CredentialsDirectoryPage,
      },
      {
        route: consts.CREDENTIALS(formatMessage),
        subroute: '/:id',
        exact: false,
        component: CredentialPage,
      },
    ]);
  }

  if (oneOnOnesEnabled) {
    routes = routes.concat([
      {
        route: consts.TEAM_ONE_ON_ONES(formatMessage),
        component: OneOnOnesDirectoryPage,
      },
    ]);
  }

  if (anyContinuousFeedbackorRecognitionEnabled) {
    routes = routes.concat([
      {
        route: consts.TEAM_FEEDBACK_AND_RECOGNITION(formatMessage),
        exact: false,
        component: FeedbackDirectoryPage,
      },
    ]);
  }

  if (referralsAreEnabled) {
    routes = routes.concat([
      {
        route: consts.REFERRALS(formatMessage),
        exact: true,
        component: Referrals,
      },
    ]);
  }

  // Keep the vanity URL route at the end so that user generated slugs
  // don't conflict with any of the routes above
  routes = routes.concat([
    {
      route: consts.HOME(formatMessage),
      subroute: ':slug',
      exact: false,
      component: PersonProfile,
    },
  ]);

  return routes.map((r) => ({
    path: r.subroute ? r.route.path + r.subroute : r.route.path,
    name: r.route.name,
    exact: typeof r.exact !== 'undefined' ? r.exact : true,
    component: r.component,
    permissionsCallback: r.permissionsCallback,
  }));

  // NOTE: when needing to authorize for additional Google API access, enable the below
  /*.map(r => ({
    ...r,
    authParams: {
      access_type: 'offline',
      approval_prompt: 'force',
      // NOTE: this needs to match what is in google.py on the backend, so when updating
      // this, update there as well
      // TODO: unify these into one place
      // NOTE: THIS LIST HERE IS COMMA-SEPARATED, BUT ON THE BACKEND IT'S AN ARRAY
      connection_scope: 'https://www.googleapis.com/auth/admin.directory.user.readonly,https://www.googleapis.com/auth/admin.directory.group.readonly,https://www.googleapis.com/auth/admin.directory.group.member.readonly,https://www.googleapis.com/auth/admin.directory.user.readonly,https://www.googleapis.com/auth/admin.directory.orgunit.readonly',
    },
  }));*/
};

export default generateRoutes;
